import { useState } from 'react';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import profileImage from '../new_profile.jpg';
import Tabs from '../components/Tabs';

function Home() {
  const [expandedPaper, setExpandedPaper] = useState(null);

  const togglePaper = (paperId) => {
    setExpandedPaper(expandedPaper === paperId ? null : paperId);

    if (expandedPaper !== paperId) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
  };

  return (
    <main className="content">
      <Tabs />
      <div className="header-section">
        <div className="profile-section">
          <img src={profileImage} alt="Ben Shi" className="profile-picture" />
          <div className="name-section">
            <h1 className="greeting">
              Ben Shi
              <div className="chinese-name">石泉 (Shí Quán)</div>
            </h1>
          </div>
        </div>
        <div className="social-links">
          <a href="https://scholar.google.com/citations?user=6jLhcpoAAAAJ&hl=en" target="_blank" rel="noopener noreferrer" title="Google Scholar">
            <span className="social-icon">GS</span>
          </a>
          <a href="https://github.com/benshi34" target="_blank" rel="noopener noreferrer" title="GitHub">
            <span className="social-icon">gh</span>
          </a>
          <a href="https://x.com/benshi34" target="_blank" rel="noopener noreferrer" title="Twitter">
            <span className="social-icon">tw</span>
          </a>
        </div>
      </div>

      <p>
        Hi, my name is Ben. I am a CS PhD student at <a href="https://nlp.stanford.edu/" target="_blank" rel="noopener noreferrer">Stanford NLP</a>. In my past life I was an undergraduate student at <a href="https://www.princeton.edu/" target="_blank" rel="noopener noreferrer">Princeton</a>, advised by Professor <a href="https://www.cs.princeton.edu/~karthikn/" target="_blank" rel="noopener noreferrer">Karthik Narasimhan</a> and Dr. <a href="https://ysymyth.github.io/" target="_blank" rel="noopener noreferrer">Shunyu Yao</a>.
      </p>

      <p>
        In my free time I like to produce music and sing. I do not claim to be good at it.
      </p>

      <p>
        For recent work, please check out <Link to="/blog/homecoming">Homecoming</Link> and <Link to="/blog/impersona-env">Impersona-Env</Link>!
      </p>

      <div className="recent-goings">
        <h2>Recently</h2>
        <div className="timeline">
          <div className="timeline-item">
            <span className="date">Oct '26</span>
            <span className="event">Homecoming <Link to="/blog/homecoming">[link]</Link>, Impersona-Env <Link to="/blog/impersona-env">[link]</Link></span>
          </div>
          <div className="timeline-item">
            <span className="date">Sep '26</span>
            <span className="event">τ<sup>τ</sup>-bench is out! See <a href="https://sierra.ai/fr/blog/hyper-t-bench-evaluating-agents-that-build-agents" target="_blank" rel="noopener noreferrer">blog</a></span>
          </div>
          <div className="timeline-item">
            <span className="date">April '26</span>
            <span className="event">Honored to have received the NSF GRFP Fellowship to continue my research at Stanford NLP.</span>
          </div>
          <div className="timeline-item">
            <span className="date">Oct '25</span>
            <span className="event">Check out new blog posts on <a href="https://sierra.ai/blog/voice-post-training" target="_blank" rel="noopener noreferrer">voice agent post training</a> and <a href="https://sierra.ai/blog/t-bench-leaderboard" target="_blank" rel="noopener noreferrer">τ-bench</a> authored by me.</span>
          </div>
        </div>
      </div>

      <p>
        Feel free to contact me at <code>qbshi@alumni.princeton.edu</code>.
      </p>

      <section className="selected-works">
        <h2>Selected Works</h2>
        <p className="clickable-hint">you can just click on things, you know?</p>
        <div className="paper">
          <h3 className="paper-title clickable" onClick={() => togglePaper('hyper-tau')}>
            τ<sup>τ</sup>-Bench: An Environment for End-To-End, Realistic Agent Construction
          </h3>
          {expandedPaper === 'hyper-tau' && (
            <p className="paper-description">
              The work of building agents is increasingly handed to coding agents, but no benchmark asks whether they can actually deliver one under the conditions of a real client engagement. τ<sup>τ</sup>-bench (pronounced "hyper-tau-bench") makes agent construction itself the task: a developer agent gets the records a business actually keeps, a client who holds the requirements, a possibly defective production API, a codebase to inherit, and hard limits on serving cost and models. The agent it builds is then deployed against held-out simulated users. Across 53 tasks in four domains, the strongest configuration passes just 23.9%, against an expert-authored ceiling of 82.2%.
            </p>
          )}
          <p className="paper-authors"><strong>Quan Shi</strong>, Keshav Dhandhania, Karthik Narasimhan, Victor Barres</p>
          <p className="paper-venue">Preprint 2026</p>
          <div className="paper-links">
            <a href="https://arxiv.org/abs/2609.04611" target="_blank" rel="noopener noreferrer">Paper@ArXiv</a>
            <a href="https://sierra.ai/blog/hyper-t-bench-evaluating-agents-that-build-agents" target="_blank" rel="noopener noreferrer">Blog</a>
            <a href="https://sierra-research.github.io/hyper-tau-bench/" target="_blank" rel="noopener noreferrer">Leaderboard</a>
            <a href="https://github.com/sierra-research/hyper-tau-bench" target="_blank" rel="noopener noreferrer">Code@GitHub</a>
          </div>
        </div>

        <div className="paper">
          <h3 className="paper-title clickable" onClick={() => togglePaper('tau-knowledge')}>
            τ-Knowledge: Evaluating Conversational Agents over Unstructured Knowledge
          </h3>
          {expandedPaper === 'tau-knowledge' && (
            <p className="paper-description">
              Conversational agents are increasingly deployed in knowledge-intensive settings, yet most benchmarks evaluate retrieval or tool use independently. We introduce τ-Knowledge, an extension of τ-Bench for evaluating agents in environments where success depends on coordinating external, natural-language knowledge with tool outputs. Our new domain, τ-Banking, models realistic fintech customer support workflows over ~700 interconnected knowledge documents. Even frontier models with high reasoning budgets achieve only ~25.5% pass^1, with reliability degrading sharply over repeated trials.
            </p>
          )}
          <p className="paper-authors"><strong>Quan Shi*</strong>, Alexandra Zytek*, Pedram Razavi, Karthik Narasimhan, Victor Barres</p>
          <p className="paper-venue">
            ICML 2026
            <a className="venue-bubble" href="https://artificialanalysis.ai/#intelligence" target="_blank" rel="noopener noreferrer">📊 Artificial Analysis Intelligence Index</a>
            <a className="venue-bubble" href="https://thinkingmachines.ai/news/introducing-inkling/" target="_blank" rel="noopener noreferrer">🧠 Inkling release</a>
            <a className="venue-bubble" href="https://openai.com/index/gpt-5-6/" target="_blank" rel="noopener noreferrer">✨ GPT-5.6 release</a>
          </p>
          <div className="paper-links">
            <a href="https://arxiv.org/abs/2603.04370" target="_blank" rel="noopener noreferrer">Paper@ArXiv</a>
            <a href="https://github.com/sierra-research/tau2-bench/tree/dev/tau3" target="_blank" rel="noopener noreferrer">Code@GitHub</a>
          </div>
        </div>

        <div className="paper">
          <h3 className="paper-title clickable" onClick={() => togglePaper('knowledge-transfer')}>
            When Models Know More Than They Can Explain: Quantifying Knowledge Transfer in Human-AI Collaboration
          </h3>
          {expandedPaper === 'knowledge-transfer' && (
            <p className="paper-description">
              As we optimize model reasoning over verifiable objectives, how does this affect human understanding of said reasoning to achieve superior collaborative outcomes? In our new preprint, we conduct the first large-scale study to isolate and quantify models' ability to collaborate and transfer knowledge to humans.
            </p>
          )}
          <p className="paper-authors"><strong>Quan Shi</strong>, Carlos E. Jimenez, Shunyu Yao, Nick Haber, Diyi Yang, Karthik Narasimhan</p>
          <p className="paper-venue">
            NeurIPS 2025, XLLM-Reason-Plan @ COLM 2025
            <span className="award-bubble">🏆 Special recognition award</span>
          </p>
          <div className="paper-links">
            <a href="https://arxiv.org/abs/2506.05579" target="_blank" rel="noopener noreferrer">Paper@ArXiv</a>
            <a href="https://kite-live.vercel.app" target="_blank" rel="noopener noreferrer">Project Website</a>
            <a href="https://github.com/benshi34/q-knowledge-transfer" target="_blank" rel="noopener noreferrer">Code@GitHub</a>
            <a href="https://x.com/BenShi34/status/1932457154293764399" target="_blank" rel="noopener noreferrer">Twitter Thread</a>
          </div>
        </div>

        <div className="paper">
          <h3 className="paper-title clickable" onClick={() => togglePaper('impersona')}>
            IMPersona: Evaluating Individual Level LM Impersonation
          </h3>
          {expandedPaper === 'impersona' && (
            <p className="paper-description">
              Language models can pass the Turing test, but can they imitate humans on the individual level? We train models on decades of text data, finding that with minimal compute and inference-time techniques, even close friends and family fail to distinguish between human and AI-generated responses. Big implications for data privacy.
            </p>
          )}
          <p className="paper-authors"><strong>Quan Shi</strong>, Carlos E. Jimenez, Stephen Dong, Brian Seo, Caden Yao, Adam Kelch, Karthik Narasimhan</p>
          <p className="paper-venue">COLM 2025, SoLaR @ COLM 2025</p>
          <div className="paper-links">
            <a href="https://arxiv.org/abs/2504.04332" target="_blank" rel="noopener noreferrer">Paper@ArXiv</a>
            <a href="https://impersona-website.vercel.app/" target="_blank" rel="noopener noreferrer">Project Website</a>
            <a href="https://github.com/princeton-nlp/impersona" target="_blank" rel="noopener noreferrer">Code@GitHub</a>
            <a href="https://x.com/BenShi34/status/1942784715330838836" target="_blank" rel="noopener noreferrer">Twitter Thread</a>
          </div>
        </div>

        <div className="paper">
          <h3 className="paper-title clickable" onClick={() => togglePaper('olympiad')}>
            Can Language Models Solve Olympiad Programming?
          </h3>
          {expandedPaper === 'olympiad' && (
            <p className="paper-description">
              Competitive programming represents one of the most challenging reasoning tasks for AI systems. We systematically evaluate language models on USACO problems, introducing techniques like episodic retrieval and reflection that significantly improve performance on these complex algorithmic challenges.
            </p>
          )}
          <p className="paper-authors"><strong>Quan Shi*</strong>, Michael Tang*, Karthik Narasimhan, Shunyu Yao</p>
          <p className="paper-venue">COLM 2024</p>
          <div className="paper-links">
            <a href="https://arxiv.org/abs/2404.10952" target="_blank" rel="noopener noreferrer">Paper@ArXiv</a>
            <a href="https://princeton-nlp.github.io/USACOBench/" target="_blank" rel="noopener noreferrer">Project Website</a>
            <a href="https://github.com/princeton-nlp/USACO" target="_blank" rel="noopener noreferrer">Code@GitHub</a>
            <a href="https://langchain-ai.github.io/langgraph/tutorials/usaco/usaco/" target="_blank" rel="noopener noreferrer">LangGraph Tutorial</a>
            <a href="https://mobile.x.com/ShunyuYao12/status/1780848531051544620" target="_blank" rel="noopener noreferrer">Twitter Thread</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
